import * as React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./hooks/useAuth";
import Index from "./pages/Index";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";
import MobileAppShell from "./components/mobile/MobileAppShell";

// Lazy-load all non-homepage routes to shrink initial JS bundle.
// The homepage (Index) remains eagerly loaded for fast LCP.
const Listings = React.lazy(() => import("./pages/Listings"));
const ListingDetails = React.lazy(() => import("./pages/ListingDetails"));
const SellerDashboard = React.lazy(() => import("./pages/SellerDashboard"));
const BuyerDashboard = React.lazy(() => import("./pages/BuyerDashboard"));
const BuyerChat = React.lazy(() => import("./pages/BuyerChat"));
const SellerChat = React.lazy(() => import("./pages/SellerChat"));
const PropertyChat = React.lazy(() => import("./pages/PropertyChat"));
const Concierge = React.lazy(() => import("./pages/Concierge"));
const ProfessionalDashboard = React.lazy(() => import("./pages/ProfessionalDashboard"));
const ProfessionalProfile = React.lazy(() => import("./pages/ProfessionalProfile"));
const ProfessionalSchedule = React.lazy(() => import("./pages/ProfessionalSchedule"));
const PublicCalendar = React.lazy(() => import("./pages/PublicCalendar"));
const ListProperty = React.lazy(() => import("./pages/ListProperty"));
const EditListing = React.lazy(() => import("./pages/EditListing"));
const Professionals = React.lazy(() => import("./pages/Professionals"));
const ProfessionalListings = React.lazy(() => import("./pages/ProfessionalListings"));
const ProfessionalDetail = React.lazy(() => import("./pages/ProfessionalDetail"));
const About = React.lazy(() => import("./pages/About"));
const Contact = React.lazy(() => import("./pages/Contact"));
const Blog = React.lazy(() => import("./pages/Blog"));
const BlogPost = React.lazy(() => import("./pages/BlogPost"));
const BlogAnalytics = React.lazy(() => import("./pages/BlogAnalytics"));
const Privacy = React.lazy(() => import("./pages/Privacy"));
const Auth = React.lazy(() => import("./pages/Auth"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
const ProfessionalAuth = React.lazy(() => import("./pages/ProfessionalAuth"));
const ProfessionalSignup = React.lazy(() => import("./pages/ProfessionalSignup"));
const BuilderDashboard = React.lazy(() => import("./pages/BuilderDashboard"));
const BuilderAuth = React.lazy(() => import("./pages/BuilderAuth"));
const TeamInvite = React.lazy(() => import("./pages/TeamInvite"));
const OrgInvite = React.lazy(() => import("./pages/OrgInvite"));
const RescheduleAppointment = React.lazy(() => import("./pages/RescheduleAppointment"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const InvoicePayment = React.lazy(() => import("./pages/InvoicePayment"));
const EmbedFormPreview = React.lazy(() => import("./pages/EmbedFormPreview"));
const Library = React.lazy(() => import("./pages/Library"));
const ThankYou = React.lazy(() => import("./pages/ThankYou"));
const ContactThankYou = React.lazy(() => import("./pages/ContactThankYou"));
const ClaimHome = React.lazy(() => import("./pages/ClaimHome"));
const ScheduleDemo = React.lazy(() => import("./pages/ScheduleDemo"));
const SavedHomes = React.lazy(() => import("./pages/SavedHomes"));


const queryClient = new QueryClient();

const App: React.FC = () => (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
          <ScrollToTop />
          <React.Suspense fallback={null}>
          <MobileAppShell />
          <main className="md:pb-0 pb-16">
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/listings" element={<Listings />} />
            <Route path="/listings/:id" element={<ListingDetails />} />
            
            
            <Route path="/seller-dashboard" element={
              <ProtectedRoute requiredRole="seller">
                <SellerDashboard />
              </ProtectedRoute>
            } />
            <Route path="/buyer-dashboard" element={
              <ProtectedRoute requiredRole="buyer">
                <BuyerDashboard />
              </ProtectedRoute>
            } />
          <Route path="/professional-dashboard" element={
            <ProtectedRoute requiredRole="professional">
              <ProfessionalDashboard />
            </ProtectedRoute>
          } />
          <Route path="/builder-dashboard" element={
            <ProtectedRoute>
              <BuilderDashboard />
            </ProtectedRoute>
          } />
          <Route path="/builder-auth" element={<BuilderAuth />} />
          <Route path="/team-invite/:token" element={<TeamInvite />} />
          <Route path="/org-invite/:token" element={<OrgInvite />} />
            <Route path="/professional-profile" element={
              <ProtectedRoute requiredRole="professional">
                <ProfessionalProfile />
              </ProtectedRoute>
            } />
            <Route path="/professional-schedule" element={
              <ProtectedRoute requiredRole="professional">
                <ProfessionalSchedule />
              </ProtectedRoute>
            } />
            <Route path="/calendar/:professionalId" element={<PublicCalendar />} />
            <Route path="/professionals" element={<Professionals />} />
            <Route path="/professional-listings" element={<ProfessionalListings />} />
            <Route path="/professional-listings/:id" element={<ProfessionalDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/admin/blog-analytics" element={<BlogAnalytics />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/property-chat" element={<PropertyChat />} />
            <Route path="/concierge" element={<Concierge />} />
            <Route path="/buyer-chat" element={
              <ProtectedRoute requiredRole="buyer">
                <BuyerChat />
              </ProtectedRoute>
            } />
            <Route path="/seller-chat" element={
              <ProtectedRoute requiredRole="seller">
                <SellerChat />
              </ProtectedRoute>
            } />
            <Route path="/list-property" element={
              <ProtectedRoute>
                <ListProperty />
              </ProtectedRoute>
            } />
            <Route path="/edit-listing/:id" element={
              <ProtectedRoute>
                <EditListing />
              </ProtectedRoute>
            } />
             <Route path="/auth" element={<Auth />} />
             <Route path="/reset-password" element={<ResetPassword />} />
             <Route path="/claim-home" element={<ClaimHome />} />
            <Route path="/schedule-demo" element={<ScheduleDemo />} />
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/contact-thank-you" element={<ContactThankYou />} />
            <Route path="/professional-auth" element={<ProfessionalAuth />} />
            <Route path="/professional-signup" element={<ProfessionalSignup />} />
             <Route path="/reschedule/:appointmentId" element={<RescheduleAppointment />} />
             <Route path="/invoice-payment/:invoiceId" element={<InvoicePayment />} />
             <Route path="/embed/form/:professionalId" element={<EmbedFormPreview />} />
             <Route path="/library" element={
               <ProtectedRoute>
                 <Library />
               </ProtectedRoute>
             } />
             <Route path="/saved-homes" element={
               <ProtectedRoute>
                 <SavedHomes />
               </ProtectedRoute>
             } />
             <Route path="*" element={<NotFound />} />
          </Routes>
          </main>
          </React.Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;